// SMPTE タイムコード表示用の video.js カスタムコンポーネント登録。
// Videojs.vue（Composition API）と Clip*Component.vue（Options API）の両方から共用する。
// registerMoreButton()（Videojs.vue）と同じ多重登録防止パターンを使う。

import { computeCurrentTimecode } from './timecode';

/**
 * @param {typeof import('video.js').default} videojs
 */
export function registerTimecodeComponents(videojs) {
  if (!videojs.getComponent('TimecodeToggleButton')) {
    const Button = videojs.getComponent('Button');

    class TimecodeToggleButton extends Button {
      constructor(parent, options) {
        super(parent, options);
        this.controlText('タイムコード表示');
      }

      createEl(tag, props, attributes) {
        const el = super.createEl(tag, props, attributes);
        const iconPlaceholder = el.querySelector('.vjs-icon-placeholder');
        if (iconPlaceholder) {
          iconPlaceholder.textContent = 'TC';
        }
        return el;
      }

      buildCSSClass() {
        return `vjs-timecode-toggle ${super.buildCSSClass()}`;
      }

      handleClick() {
        if (typeof this.options_.onToggle === 'function') {
          this.options_.onToggle();
          return;
        }
        this.player().toggleClass('vjs-timecode-active');
      }
    }

    videojs.registerComponent('TimecodeToggleButton', TimecodeToggleButton);
  }

  if (!videojs.getComponent('TimecodeDisplay')) {
    const Component = videojs.getComponent('Component');

    class TimecodeDisplay extends Component {
      createEl() {
        const el = super.createEl('div', {
          className: 'vjs-timecode-display',
        });
        this.textEl_ = document.createElement('span');
        el.appendChild(this.textEl_);
        return el;
      }

      setTimecodeText(text) {
        if (this.textEl_) {
          this.textEl_.textContent = text || '';
        }
      }
    }

    videojs.registerComponent('TimecodeDisplay', TimecodeDisplay);
  }
}

/**
 * TimeTooltip インスタンスの updateTime() をラップし、
 * player に vjs-timecode-active クラスが付与されている間だけ
 * 表示内容を SMPTE タイムコード文字列に差し替える。
 * クラスプロトタイプは汚染せず、インスタンスにのみパッチする（player.dispose() で自然に解放される）。
 *
 * @param {import('video.js').default.Component} tooltip TimeTooltip コンポーネントのインスタンス
 * @param {() => {startTimecode: string, frameRate: string|number, clipOffsetSeconds: number}} getTimecodeContext
 */
function patchTimeTooltip(tooltip, getTimecodeContext) {
  if (!tooltip || tooltip.__timecodePatched) {
    return;
  }
  tooltip.__timecodePatched = true;

  const originalUpdateTime = tooltip.updateTime.bind(tooltip);

  tooltip.updateTime = function timecodeAwareUpdateTime(seekBarRect, seekBarPoint, time, cb) {
    const player = tooltip.player();
    const isTimecodeActive = player && player.hasClass('vjs-timecode-active');

    if (isTimecodeActive) {
      const context = getTimecodeContext ? getTimecodeContext() : null;
      if (context && context.startTimecode) {
        const offsetSeconds = (Number(context.clipOffsetSeconds) || 0) + (Number(time) || 0);
        const timecodeText = computeCurrentTimecode(context.startTimecode, context.frameRate, offsetSeconds);
        if (timecodeText !== null) {
          // 標準実装 (formatTime による経過時間表示) と同様に requestNamedAnimationFrame 経由で
          // 位置更新 (update) → DOM 書き込み (write) まで実行する。
          tooltip.requestNamedAnimationFrame('TimeTooltip#updateTime', () => {
            tooltip.update(seekBarRect, seekBarPoint, timecodeText);
            if (cb) {
              cb();
            }
          });
          return;
        }
      }
    }

    originalUpdateTime(seekBarRect, seekBarPoint, time, cb);
  };
}

/**
 * SeekBar 上のホバー用ツールチップ（MouseTimeDisplay 配下）と
 * 再生ヘッド用ツールチップ（PlayProgressBar 配下、存在する場合）の TimeTooltip に
 * タイムコード表示パッチを適用する。
 *
 * @param {import('video.js').default.Player} player
 * @param {() => {startTimecode: string, frameRate: string|number, clipOffsetSeconds: number}} getTimecodeContext
 */
export function bindSeekBarTimecodeTooltip(player, getTimecodeContext) {
  if (!player) {
    return;
  }
  const controlBar = player.getChild('ControlBar');
  const seekBar = controlBar && controlBar.getChild('ProgressControl') && controlBar.getChild('ProgressControl').getChild('SeekBar');
  if (!seekBar) {
    return;
  }

  const mouseTimeDisplay = seekBar.getChild('MouseTimeDisplay');
  if (mouseTimeDisplay) {
    patchTimeTooltip(mouseTimeDisplay.getChild('TimeTooltip'), getTimecodeContext);
  }

  const playProgressBar = seekBar.getChild('PlayProgressBar');
  if (playProgressBar) {
    patchTimeTooltip(playProgressBar.getChild('TimeTooltip'), getTimecodeContext);
  }
}
